<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="800px"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="info">mdi-note-text</v-icon>
        <span>Observações do Projeto</span>
        <v-spacer></v-spacer>
        <v-btn
          icon
          @click="closeDialog"
          :disabled="isLoading"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-0" style="height: 500px;">
        <div class="pa-4 bg-grey-lighten-5">
          <div class="d-flex align-center">
            <v-icon class="mr-2" color="primary">mdi-folder</v-icon>
            <div>
              <div class="text-h6">{{ projectName }}</div>
              <div class="text-caption text--secondary">
                Cliente: {{ clientName }}
              </div>
            </div>
          </div>
        </div>

        <div class="pa-4 border-b">
          <v-form ref="noteForm" v-model="noteFormValid">
            <v-textarea
              v-model="newNoteContent"
              label="Nova observação"
              placeholder="Digite sua observação sobre o projeto..."
              rows="3"
              outlined
              dense
              :rules="noteRules"
              :disabled="isLoading"
              counter="1000"
              maxlength="1000"
            ></v-textarea>
            
            <div class="d-flex justify-end mt-2">
              <v-btn
                color="primary"
                @click="addNote"
                :loading="isLoading"
                :disabled="!canAddNote"
                small
              >
                <v-icon left small>mdi-plus</v-icon>
                Adicionar Observação
              </v-btn>
            </div>
          </v-form>
        </div>

        <div class="pa-4" style="height: calc(100% - 200px); overflow-y: auto;">
          <div v-if="showLoadingState" class="text-center py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-2 text--secondary">Carregando observações...</div>
          </div>

          <div v-else-if="showEmptyState" class="text-center py-8">
            <v-icon size="64" color="grey-lighten-1">mdi-note-outline</v-icon>
            <div class="text-h6 mt-2 text--secondary">Nenhuma observação</div>
            <div class="text-caption text--secondary">
              Adicione a primeira observação sobre este projeto
            </div>
          </div>

          <div v-else-if="showNotesList">
            <div
              v-for="note in notesWithComputed"
              :key="note.id"
              class="mb-4"
            >
              <v-card outlined class="note-card">
                <v-card-text class="pb-2">
                  <div class="note-content mb-3">
                    {{ note.content }}
                  </div>

                  <div class="d-flex align-center justify-space-between text-caption text--secondary">
                    <div class="d-flex align-center">
                      <v-icon small class="ml-2 mr-1">mdi-clock-outline</v-icon>
                      <span>{{ note.formattedCreatedAt }}</span>
                    </div>

                    <v-menu bottom left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          v-bind="attrs"
                          v-on="on"
                          :disabled="isLoading"
                        >
                          <v-icon small>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item @click="editNote(note)">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="primary">mdi-pencil</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="text-caption">Editar</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item @click="deleteNote(note)">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="error">mdi-delete</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="text-caption">Excluir</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>

                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          text
          @click="closeDialog"
          :disabled="isLoading"
        >
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon class="mr-2" color="error">mdi-delete</v-icon>
          Confirmar Exclusão
        </v-card-title>
        <v-card-text>
          Tem certeza que deseja excluir esta observação? Esta ação não pode ser desfeita.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteDialog = false" :disabled="isLoading">
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            @click="confirmDelete"
            :loading="isLoading"
          >
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showEditDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon class="mr-2" color="primary">mdi-pencil</v-icon>
          Editar Observação
        </v-card-title>
        <v-card-text>
          <v-form ref="editForm" v-model="editFormValid">
            <v-textarea
              v-model="editingNote.content"
              label="Observação"
              rows="4"
              outlined
              dense
              :rules="noteRules"
              :disabled="isLoading"
              counter="1000"
              maxlength="1000"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showEditDialog = false" :disabled="isLoading">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmEdit"
            :loading="isLoading"
            :disabled="!canEditNote"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import ProjectService from '@/services/firebase/Projects/ProjectService.js';
import { showErrorSnackBar, showSuccessSnackBar } from '@/utils/SnackBartHelper';
import { mapGetters } from 'vuex';

export default {
  name: 'ProjectNotesDialog',
  
  props: {
    value: {
      type: Boolean,
      default: false
    },
    project: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      isLoading: false,
      notes: [],
      newNoteContent: '',
      noteFormValid: false,
      editFormValid: false,
      showDeleteDialog: false,
      showEditDialog: false,
      noteToDelete: null,
      editingNote: { content: '' },
      noteRules: [
        v => !!v || 'Observação é obrigatória',
        v => (v && v.length >= 3) || 'Observação deve ter pelo menos 3 caracteres',
        v => (v && v.length <= 1000) || 'Observação deve ter no máximo 1000 caracteres'
      ]
    };
  },

  computed: {
    ...mapGetters(['getCurrentUser']),
    
    // Estados de carregamento e exibição
    showLoadingState() {
      return this.isLoading && this.notes.length === 0;
    },
    
    showEmptyState() {
      return !this.isLoading && this.notes.length === 0;
    },
    
    showNotesList() {
      return !this.isLoading && this.notes.length > 0;
    },
    
    // Validação do formulário
    canAddNote() {
      return this.noteFormValid && this.newNoteContent && this.newNoteContent.trim();
    },
    
    canEditNote() {
      return this.editFormValid && this.editingNote.content && this.editingNote.content.trim();
    },
    
    // Dados do usuário atual
    currentUser() {
      return {
        id: this.getCurrentUser?.id || 'unknown',
        name: this.getCurrentUser?.name || 'Usuário',
        email: this.getCurrentUser?.email || 'unknown@email.com'
      };
    },
    
    // Informações do projeto
    projectName() {
      return this.project?.project_name || 'Projeto';
    },
    
    clientName() {
      return this.project?.lead_name || 'N/A';
    },
    
    // Computed para cada nota individual
    notesWithComputed() {
      return this.notes.map(note => ({
        ...note,
        formattedCreatedAt: this.formatDate(note.created_at)
      }));
    }
  },

  watch: {
    value(newVal) {
      if (newVal && this.project) {
        this.loadNotes();
      }
    },
    project: {
      handler(newVal) {
        if (newVal && this.value) {
          this.loadNotes();
        }
      },
      immediate: true
    }
  },

  methods: {
    async loadNotes() {
      if (!this.project?.id_doc) return;
      
      try {
        this.isLoading = true;
        this.notes = await ProjectService.getActiveProjectNotes(this.project.id_doc);
      } catch (error) {
        console.error('Erro ao carregar observações:', error);
        showErrorSnackBar('Erro ao carregar observações do projeto');
      } finally {
        this.isLoading = false;
      }
    },

    async addNote() {
      if (!this.canAddNote) return;
      
      try {
        this.isLoading = true;
        
        await ProjectService.addProjectNote(
          this.project.id_doc,
          this.newNoteContent
        );
        
        this.newNoteContent = '';
        this.$refs.noteForm?.reset();
        await this.loadNotes();
        
        showSuccessSnackBar('Observação adicionada com sucesso!');
      } catch (error) {
        console.error('Erro ao adicionar observação:', error);
        showErrorSnackBar('Erro ao adicionar observação');
      } finally {
        this.isLoading = false;
      }
    },

    editNote(note) {
      this.editingNote = { ...note };
      this.showEditDialog = true;
    },

    async confirmEdit() {
      if (!this.canEditNote) return;
      
      try {
        this.isLoading = true;
        
        await ProjectService.updateProjectNote(
          this.project.id_doc,
          this.editingNote.id,
          this.editingNote.content
        );
        
        this.showEditDialog = false;
        await this.loadNotes();
        
        showSuccessSnackBar('Observação atualizada com sucesso!');
      } catch (error) {
        console.error('Erro ao atualizar observação:', error);
        showErrorSnackBar('Erro ao atualizar observação');
      } finally {
        this.isLoading = false;
      }
    },

    deleteNote(note) {
      this.noteToDelete = note;
      this.showDeleteDialog = true;
    },

    async confirmDelete() {
      if (!this.noteToDelete) return;
      
      try {
        this.isLoading = true;
        
        await ProjectService.deleteProjectNote(
          this.project.id_doc,
          this.noteToDelete.id
        );
        
        this.showDeleteDialog = false;
        this.noteToDelete = null;
        await this.loadNotes();
        
        showSuccessSnackBar('Observação excluída com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir observação:', error);
        showErrorSnackBar('Erro ao excluir observação');
      } finally {
        this.isLoading = false;
      }
    },

    closeDialog() {
      this.$emit('close');
      this.$emit('input', false);
    },

    formatDate(timestamp) {
      if (!timestamp) return 'Data não disponível';
      
      try {
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        console.error('Erro ao formatar data:', error);
        return 'Data inválida';
      }
    }
  }
};
</script>

<style scoped>
.note-card {
  transition: box-shadow 0.2s;
}

.note-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.note-content {
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.5;
}

.border-b {
  border-bottom: 1px solid #e0e0e0;
}
</style>
